import React, { useState, useRef, useEffect } from "react";
import {
  createPColor,
  deletePColor,
  getPColorAll,
  updatePColor,
  softDeletePColor,
  getLanguages,
} from "../../Services/ProductColorService";
import { getColorAll } from "../../Services/ColorService";
import { getCountryAll } from "../../Services/CountryService";
import { useForm, Controller } from "react-hook-form";
import Swal from "sweetalert2";
import MTable from "../../Components/MTable/MTable";
import { InputSwitch } from "primereact/inputswitch";
import Overlay from "../../Components/Overlay/Overlay";
import { handlerFormError, uuid } from "../../Utils/Utils";
import { toastError, toastSuccess } from "../../Components/Toasts/Toast";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import EditButton from "../../Components/Button/EditButton";
import DeleteButton from "../../Components/Button/DeleteButton";

let processingId = -1;

const ProductColorPage = () => {
  let { userInfo } = useSelector((state) => state.auth);
  const [showAll, setShowAll] = useState(false);
  const mTable = useRef();
  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      master_color_id: "",
      color: "",
      country_code: "",
      language_code: "",
      is_active: true,
    },
  });

  const [state, setState] = useState({
    colors: [],
    countries: [],
    languages: [],
    showForm: false,
    productColor: {
      master_color_id: "",
      color: "",
      country_code: "",
      language_code: "",
      is_active: true,
    },
    processing: false,
  });

  const startProcessing = () => {
    processingId = setTimeout(() => {
      setState({ ...state, processing: true });
    }, 150);
  };

  const stopProcessing = () => {
    clearTimeout(processingId);
    setState({ ...state, processing: false });
  };

  const columns = [
    { id: uuid(), title: "Color", field: "color", sortable: true },
    {
      id: uuid(),
      title: "Country",
      field: "country_name",
      sortable: true,
      render: (item) => {
        return item.country_name ? (
          item.country_name
        ) : (
          <p className="text-red">Not Found</p>
        );
      },
    },
    { id: uuid(), title: "Language", field: "language_name", sortable: true },
    {
      id: uuid(),
      title: "Active",
      field: "is_active",
      render: (item) => {
        if (item.is_active === true) {
          return <span className="badge badge-success">Active</span>;
        } else {
          return <span className="badge badge-secondary">Inactive</span>;
        }
      },
    },
    {
      id: uuid(),
      title: "",
      sortable: true,
      style: { width: 200 },
      render: (item) => {
        return (
          <div>
            {userInfo.access.product_color.can_update && (
              <EditButton title="Edit" onClick={onEdit(item)} />
            )}
            {userInfo.access.product_color.can_delete && (
              <DeleteButton title="Remove" onClick={onRemove(item)} />
            )}
          </div>
        );
      },
    },
  ];

  const onActiveChange = (item) => (e) => {
    if (!userInfo.access.product_color.can_update) {
      toastError("You dont have permission for this action");
      return;
    }

    const status = e.target.value ? "Activate" : "Inactive";
    softDeletePColor(item.id, { is_active: e.target.value })
      .then((res) => {
        toastSuccess(`${status} ${item.color} Success.`);
        mTable.current.refresh();
      })
      .catch(({ response: { err } }) => {
        toastError(`${status} Failed.`);
      });
  };

  const removeData = (id) => {
    deletePColor(
      id,
      (res) => {
        if (res.status == 200 || res.status == 201) {
          Swal.fire({
            icon: "success",
            title: "Delete data success",
            text: "Data has been deleted!",
          }).then((res) => {
            mTable.current.refresh();
          });
        }
      },
      ({ response: { data } }) => {
        Swal.fire({
          icon: "error",
          title: "Delete data fail",
          text: data.message,
        });
      }
    );
  };

  const onAddData = () => {
    if (!userInfo.access.product_color.can_create) {
      return;
    }
    onReset();
    setState({ ...state, showForm: true });
  };

  const onEdit = (item) => () => {
    if (!userInfo.access.product_color.can_update) {
      return;
    }

    setValue("master_color_id", item.master_color_id);
    setValue("color", item.color);
    setValue("country_code", item.country_code);
    setValue("language_code", item.language_code);
    setValue("is_active", item.is_active);
    reset(item);
    setState({ ...state, productColor: item, showForm: true });
  };

  const onRemove = (item) => () => {
    if (!userInfo.access.product_color.can_delete) {
      toastError("You dont have permission for this action");
      return;
    }

    Swal.fire({
      icon: "question",
      title: "Are you sure?",
      text: "Deleted data can not be restored!",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
    }).then(({ isConfirmed }) => {
      if (isConfirmed) {
        removeData(item.id);
      }
    });
  };

  const onSubmit = (data) => {
    startProcessing();
    const response = productColor.id
      ? updatePColor(productColor.id, data)
      : createPColor(data);
    response
      .then((res) => {
        if (res.status == 200 || res.status == 201) {
          Swal.fire({
            icon: "success",
            title: "Save data success",
            text: "Data has been saved!",
          }).then((r) => {
            mTable.current.refresh();
            productColor.id ? onCloseForm() : onReset();
          });
        }
      })
      .catch(({ response: { data } }) => {
        handlerFormError(data);
      })
      .finally((_) => stopProcessing());
  };

  const onReset = () => {
    reset({
      master_color_id: "",
      color: "",
      country_code: "",
      language_code: "",
      is_active: true,
    });
    setState({ ...state, productColor: {} });
  };

  const onCloseForm = () => {
    reset({
      master_color_id: "",
      color: "",
      country_code: "",
      language_code: "",
      is_active: true,
    });
    setState({ ...state, productColor: {}, showForm: false });
  };

  const { colors, countries, languages, productColor, processing, showForm } =
    state;

  const showAllFunc = () => {
    setShowAll(!showAll);
    mTable.current.refresh();
  };

  const propsTable = {
    columns,
    showAllState: showAll,
    showAllFunc,
    getData: (params) => {
      if (showAll === false) {
        if (params.filterAnd) params.filterAnd += "," + `is_active:true`;
        else params.filterAnd = "is_active:true";
      }
      return getPColorAll({ ...params });
    },
    enableFilter: true,
    showAddButton: !userInfo.access.product_color.can_create ? false : true,
  };

  useEffect(() => {
    const reqColor = getColorAll({ orders: "name:asc", perpage: 1000 }).then(
      (res) => res.data.data
    );
    const reqCountry = getCountryAll({
      orders: "country_name:asc",
      perpage: 1000,
    }).then((res) => res.data.data);
    const reqLanguage = getLanguages({ perpage: 1000 }).then(
      (res) => res.data.data
    );

    Promise.all([reqColor, reqCountry, reqLanguage]).then((results) => {
      const [_colors, _countries, _languages] = results;
      setState({
        ...state,
        colors: _colors,
        countries: _countries,
        languages: _languages,
      });
    });
  }, []);

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h4
                style={{
                  marginTop: "0.5rem",
                  marginLeft: "0.2rem",
                  fontWeight: "400",
                }}
              >
                {showForm ? "Product Color Form" : "Product Color Management"}
              </h4>
              <h6
                style={{
                  marginLeft: "0.2rem",
                  fontWeight: "200",
                  letterSpacing: "1.2px",
                }}
              >
                {showForm
                  ? "Add/Edit a Product Color"
                  : "Database of Product Colors"}
              </h6>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <NavLink to="/home">Home</NavLink>
                </li>
                <li className="breadcrumb-item active">Product Color</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <Overlay display={processing} />

                <form name="form-detail" onSubmit={handleSubmit(onSubmit)}>
                  <div className="card-body">
                    <div
                      className={`${
                        !showForm
                          ? "card__side--front-show"
                          : "card__side--front"
                      }`}
                    >
                      <MTable
                        ref={mTable}
                        {...propsTable}
                        onAddData={onAddData}
                        showIndex={true}
                      />
                    </div>

                    <div
                      className={`${
                        showForm ? "card__side--back-show" : "card__side--back"
                      }`}
                    >
                      <div className="row">
                        <div className="col-md-4">
                          {
                            //    productColor.language_code == '' &&
                            <div className="form-group">
                              <label>Base Color</label>
                              <select
                                id="master_color_id"
                                className="form-control"
                                {...register("master_color_id", {
                                  required: {
                                    value: true,
                                    message: "Base Color is required!",
                                  },
                                })}
                              >
                                <option value={""}>Select Base Color</option>
                                {colors.map((item, i) => (
                                  <option key={i} value={item.id}>
                                    {item.name}
                                  </option>
                                ))}
                              </select>
                              {errors.master_color_id && (
                                <span
                                  className="text-danger"
                                  style={{ fontSize: 14 }}
                                >
                                  {errors.master_color_id.message}
                                </span>
                              )}
                            </div>
                          }

                          <div className="form-group">
                            <label>Country</label>
                            <select
                              id="country_code"
                              className="form-control"
                              {...register("country_code", {
                                required: {
                                  value: true,
                                  message: "Country is required!",
                                },
                              })}
                            >
                              <option value={""}>Select Country</option>
                              {countries.map((item, i) => (
                                <option key={i} value={item.country_code}>
                                  {item.country_name}
                                </option>
                              ))}
                            </select>
                            {errors.country_code && (
                              <span
                                className="text-danger"
                                style={{ fontSize: 14 }}
                              >
                                {errors.country_code.message}
                              </span>
                            )}
                          </div>

                          <div className="form-group">
                            <label>Language</label>
                            <select
                              id="language_code"
                              className="form-control"
                              {...register("language_code", {
                                required: {
                                  value: true,
                                  message: "Language is required!",
                                },
                              })}
                            >
                              <option value={""}>Select Language</option>
                              {languages.map((item, i) => (
                                <option key={i} value={item.language_code}>
                                  {item.language_name}
                                </option>
                              ))}
                            </select>
                            {errors.language_code && (
                              <span
                                className="text-danger"
                                style={{ fontSize: 14 }}
                              >
                                {errors.language_code.message}
                              </span>
                            )}
                          </div>

                          <div className="form-group">
                            <label htmlFor="sub_category_code">Color</label>
                            <input
                              id="color"
                              {...register("color", {
                                required: {
                                  value: true,
                                  message: "Color is required!",
                                },
                              })}
                              placeholder="Color"
                              className="form-control"
                            />
                            {errors.color && (
                              <span
                                className="text-danger"
                                style={{ fontSize: 14 }}
                              >
                                {errors.color.message}
                              </span>
                            )}
                          </div>

                          <div className="form-group">
                            <label htmlFor="sub-category-name">Active</label>
                            <div>
                              <Controller
                                name="is_active"
                                control={control}
                                render={({ field }) => {
                                  return (
                                    <InputSwitch
                                      {...field}
                                      checked={field.value}
                                    />
                                  );
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {showForm && (
                    <div className="card-footer delay">
                      <div className="row">
                        <div className="col-md-12">
                          <div
                            className="d-flex"
                            style={{ justifyContent: "flex-end" }}
                          >
                            <button
                              type="button"
                              className="btn btn-default mr-3"
                              style={{ width: 100 }}
                              onClick={onCloseForm}
                            >
                              <i className="fa fa-times" /> Close
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger mr-3"
                              style={{ width: 100 }}
                              onClick={onReset}
                            >
                              <i className="fa fa-sync" /> Clear
                            </button>
                            <button
                              type="submit"
                              className="btn btn-dark"
                              style={{ width: 100 }}
                              disabled={
                                control._formValues.id
                                  ? !userInfo.access.product_color.can_update
                                  : !userInfo.access.product_color.can_create
                              }
                            >
                              <i className="fa fa-save" /> Save
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductColorPage;
